import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Switch,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const BASE = process.env.REACT_APP_PATH_BULK + "/shipshopyPincode";

const PincodeServiceability = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const [addOpen, setAddOpen] = useState(false);
  const [newPincode, setNewPincode] = useState("");
  const [newOda, setNewOda] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const showSnack = (message, severity = "success") => setSnack({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE}/list`, {
        params: { page: page + 1, limit: rowsPerPage, search },
      });
      if (res.data?.success) {
        setRows(res.data.data.pincodes || []);
        setTotal(res.data.data.total || 0);
      } else {
        showSnack(res.data?.message || "Failed to load pincodes", "error");
      }
    } catch (err) {
      showSnack("Failed to load pincodes", "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value.replace(/[^0-9]/g, ""));
    setPage(0);
  };

  const handleToggleOda = async (row) => {
    try {
      const res = await axios.put(`${BASE}/${row.pincode}/oda`, { oda: !row.oda });
      if (res.data?.success) {
        setRows((prev) => prev.map((r) => (r.pincode === row.pincode ? { ...r, oda: !row.oda } : r)));
        showSnack("ODA updated");
      } else {
        showSnack(res.data?.message || "Failed to update ODA", "error");
      }
    } catch (err) {
      showSnack("Failed to update ODA", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await axios.delete(`${BASE}/${deleteTarget.pincode}`);
      if (res.data?.success) {
        showSnack("Pincode removed");
        setDeleteTarget(null);
        fetchData();
      } else {
        showSnack(res.data?.message || "Failed to remove pincode", "error");
      }
    } catch (err) {
      showSnack("Failed to remove pincode", "error");
    }
  };

  const handleAdd = async () => {
    if (!/^\d{6}$/.test(newPincode)) {
      showSnack("Enter a valid 6-digit pincode", "error");
      return;
    }
    try {
      const res = await axios.post(`${BASE}/add`, { pincode: newPincode, oda: newOda });
      if (res.data?.success) {
        showSnack("Pincode added");
        setAddOpen(false);
        setNewPincode("");
        setNewOda(false);
        fetchData();
      } else {
        showSnack(res.data?.message || "Failed to add pincode", "error");
      }
    } catch (err) {
      showSnack("Failed to add pincode", "error");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>Pincode Serviceability</Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <TextField
          label="Search by pincode"
          size="small"
          value={search}
          onChange={handleSearchChange}
          sx={{ width: 260 }}
        />
        <Button variant="contained" onClick={() => setAddOpen(true)}>Add Pincode</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pincode</TableCell>
              <TableCell>ODA</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">No pincodes found</TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.pincode}>
                <TableCell>{row.pincode}</TableCell>
                <TableCell>
                  <Switch checked={!!row.oda} onChange={() => handleToggleOda(row)} />
                  {row.oda ? "ODA" : "Non-ODA"}
                </TableCell>
                <TableCell align="right">
                  <IconButton color="error" onClick={() => setDeleteTarget(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add Pincode</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, minWidth: 300 }}>
          <TextField
            label="Pincode"
            value={newPincode}
            onChange={(e) => setNewPincode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            autoFocus
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Switch checked={newOda} onChange={(e) => setNewOda(e.target.checked)} />
            <Typography>{newOda ? "ODA" : "Non-ODA"}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove Pincode</DialogTitle>
        <DialogContent>
          Are you sure you want to remove pincode {deleteTarget?.pincode}? Orders will no longer be
          placeable on this pincode.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PincodeServiceability;
